import classNames from 'classnames';

export const thoughtBubbleArrowHeight = 8;

type ArrowProps = {
    arrowPosition: 'top' | 'bottom';
    backgroundColor: 'light-grey' | 'white';
    border?: { width: string; color: string };
};
function Arrow({ arrowPosition, backgroundColor, border }: ArrowProps) {
    const commonStyles = {
        width: `${thoughtBubbleArrowHeight * 2}px`,
        height: `${thoughtBubbleArrowHeight * 2}px`,
        borderRadius: '2px',
    };
    return (
        <div
            className={classNames('absolute inline-block', border?.color, {
                'top-0': arrowPosition === 'top',
                'bottom-0': arrowPosition === 'bottom',
                'bg-light-grey': backgroundColor === 'light-grey',
                'bg-white': backgroundColor === 'white',
            })}
            style={
                arrowPosition === 'top'
                    ? {
                          ...commonStyles,
                          borderTopWidth: border?.width,
                          borderLeftWidth: border?.width,
                          transform: `translateY(1px) rotate(45deg)`,
                      }
                    : {
                          ...commonStyles,
                          borderBottomWidth: border?.width,
                          borderRightWidth: border?.width,
                          transform: `translateY(-1px) rotate(45deg)`,
                      }
            }
        />
    );
}

type Props = {
    arrowPosition?: 'top' | 'bottom';
    backgroundColor?: 'light-grey' | 'white';
    border?: { width: string; color: string };
    padding?: string;
    children: React.ReactNode;
    className?: string;
    style?: React.CSSProperties;
    onClick?: React.MouseEventHandler;
};
export function ThoughtBubble({
    arrowPosition = 'bottom',
    padding = 'px-10',
    backgroundColor = 'light-grey',
    border,
    children,
    className,
    ...props
}: Props) {
    return (
        <div
            {...props}
            className={classNames(
                className,
                'relative inline-flex flex-col items-center',
            )}
        >
            {/* We draw the background and main borders first */}
            <div
                className={classNames(
                    'absolute rounded-[5px]',
                    padding,
                    border?.color,
                    {
                        'bg-light-grey': backgroundColor === 'light-grey',
                        'bg-white': backgroundColor === 'white',
                    },
                )}
                style={{
                    left: '0',
                    right: '0',
                    ...(arrowPosition === 'top'
                        ? {
                              top: `${thoughtBubbleArrowHeight}px`,
                              bottom: '0',
                          }
                        : {
                              bottom: `${thoughtBubbleArrowHeight}px`,
                              top: '0',
                          }),
                    ...(border == null
                        ? null
                        : {
                              borderWidth: border.width,
                          }),
                }}
            />
            {/* We then overlay the arrow, since it needs to hide the main border where the arrow is */}
            <Arrow
                border={border}
                arrowPosition={arrowPosition}
                backgroundColor={backgroundColor}
            />
            {/* We then add the content, so that it is always on top of the arrow */}
            <div
                className={classNames('relative', padding)}
                style={{
                    ...(arrowPosition === 'top'
                        ? {
                              marginTop: `${thoughtBubbleArrowHeight}px`,
                          }
                        : {
                              marginBottom: `${thoughtBubbleArrowHeight}px`,
                          }),
                }}
            >
                {children}
            </div>
        </div>
    );
}
